import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Filter } from "../model/filters/filter";
import { MatchAll } from "../model/filters/match-all";
import { EnvironmentHelper } from "./env.helper";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { Messages } from "../model/messages";
import { StatusService } from "./status.service";
import { UsersService } from "./users.service";
import { BranchService } from "./branch.service";
import { PrioritiesService } from "./priorities.service";
import { DepartmentsService } from "./departaments.service";
import { Active } from "../model/filters/active";
import { ByStatus } from "../model/filters/by-status";
import { Not } from "../model/filters/not";

@Injectable({
  providedIn: "root",
})
export class FilterService {
  public currentFilter = new BehaviorSubject<Filter>(new MatchAll([]));
  private appliedFilters: any[] = [];
  private apiUrl: string;

  constructor(
    private env: EnvironmentHelper,
    private http: HttpClient,
    private statusService: StatusService,
    private user: UsersService,
    private branch: BranchService,
    private priority: PrioritiesService,
    private department: DepartmentsService
  ) {
    const savedFilters = localStorage.getItem("appliedFilters");
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      this.statusService.makeLabel(1).subscribe((s) => {
        this.user.makeLabel(1).subscribe((a) => {});
        this.branch.makeLabel(1).subscribe((a) => {});
        this.priority.makeLabel(1).subscribe((a) => {});
        this.department.makeLabel(1).subscribe((a) => {});
        this.currentFilter.next(filters);
      });
    }
    this.apiUrl = `${env.apiBaseUrl}ticket/filter`;
  }

  addFilter(filter: Filter): void {
    if (!filter.identifier) {
      filter.identifier = Date.now();
    }

    const current = this.currentFilter.value as MatchAll;

    const newFilterCodes = this.extractCodes(filter);

    current.filters = current.filters.filter((f) => {
      const existingCodes = this.extractCodes(f);
      if (existingCodes.some((code) => newFilterCodes.includes(code))) {
        return false;
      }
      return !this.contradicts(filter, f);
    });

    current.filters.push(filter);

    this.saveFiltersToLocalStorage();
    this.currentFilter.next(current);
  }
  /**
   * Whether a filter already in the set cannot stand beside the one being
   * added.
   *
   * Until now a filter only displaced one with the same code, so 'archived'
   * landed on top of the standard filter and asked the server for tickets that
   * are archived *and* active - a thing that does not exist. The list came back
   * empty and there was no way to reach the archive at all.
   *
   * 'active' and 'archived' are the two halves of one flag, so each replaces
   * the other. And choosing 'archived' clears the whole standard filter, not
   * only its active half: the other half rules out finished tickets, and
   * finished is what most of the archive is.
   */
  private contradicts(added: Filter, existing: Filter): boolean {
    if (added.code === "archived") {
      return this.isStandardFilterPart(existing);
    }
    if (added.code === "active") {
      return existing.code === "archived";
    }
    return false;
  }

  /**
   * Whether this is one of the two filters the standard filter is made of.
   *
   * They are put there by setStandardFilter rather than by the user, and the
   * chips above the list do not show them, so nobody can see them to take them
   * off again.
   */
  public isStandardFilterPart(filter: Filter): boolean {
    if (!filter) {
      return false;
    }
    if (filter.code === "active") {
      return true;
    }
    const inner = (filter as any).filters?.[0];

    return filter.code === "not" && inner?.code === "by.status";
  }

  private extractCodes(filter: Filter): string[] {
    if (!filter) return [];

    if (Array.isArray((filter as any).filters)) {
      const innerCodes = (filter as any).filters.flatMap((f: any) =>
        this.extractCodes(f)
      );
      return innerCodes;
    }

    return [filter.code];
  }

  removeFilter(filter: Filter): void {
    const current = this.currentFilter.value as MatchAll;

    current.filters = current.filters.filter(
      (f) => f.identifier !== filter.identifier
    );

    this.restoreStandardFilter(current);
    this.saveFiltersToLocalStorage();
    this.currentFilter.next(current);
  }

  /**
   * Puts the standard filter back once nothing is asking for the archive.
   *
   * Choosing 'archived' takes the standard filter off, because the two cannot
   * both be true. Taking 'archived' off again has to undo that, or the list
   * quietly turns into every ticket that ever existed - and the two filters
   * that would have narrowed it are invisible, so there is nothing on screen to
   * explain why or to put back.
   *
   * Only when neither half is there. One half present means the standard filter
   * is already in play and this has nothing to add.
   */
  private restoreStandardFilter(current: MatchAll): void {
    const askingForTheArchive = current.filters.some(
      (f) => f.code === "archived"
    );

    if (askingForTheArchive) {
      return;
    }
    if (current.filters.some((f) => this.isStandardFilterPart(f))) {
      return;
    }
    for (const part of this.standardFilter()) {
      current.filters.push(part);
    }
  }

  /** The two filters the standard filter is made of, ready to be added. */
  private standardFilter(): Filter[] {
    const not = new Not(new Array<Filter>());
    not.filters.push(new ByStatus(3));
    not.identifier = Date.now();

    const active = new Active();
    active.identifier = Date.now() + 1;

    return [not, active];
  }

  removeFilterValue(parentFilter: Filter, valueIndex: number): void {
    const current = this.currentFilter.value as MatchAll;
    const parent = current.filters.find(f => f.identifier === parentFilter.identifier) as any;
    if (!parent) return;

    let targetArray: any[];
    if (parent.code === 'match.any') {
      targetArray = parent.filters;
    } else if (parent.code === 'not' && parent.filters?.[0]?.code === 'match.any') {
      targetArray = parent.filters[0].filters;
    } else {
      return;
    }

    targetArray.splice(valueIndex, 1);

    if (targetArray.length === 0) {
      this.removeFilter(parentFilter);
      return;
    }

    this.updateFilters([...current.filters]);
  }

  triggerFilter(): void {
    this.currentFilter.next(this.currentFilter.value);
  }

  public get filterObservable(): Observable<Filter> {
    return this.currentFilter.asObservable();
  }

  public get filterCurrent(): Filter {
    const current = this.currentFilter.value;
    return current;
  }

  private saveFiltersToLocalStorage() {
    localStorage.setItem("appliedFilters", JSON.stringify(this.filterCurrent));
  }

  clearAllFilters() {
    this.currentFilter.next(new MatchAll([]));

    this.saveFiltersToLocalStorage();
  }

  setStandardFilter() {
    this.clearAllFilters();

    for (const part of this.standardFilter()) {
      this.addFilter(part);
    }
  }
  updateFilters(filters: Filter[]) {
    const current = this.currentFilter.value as MatchAll;

    current.filters = filters;

    this.restoreStandardFilter(current);
    this.saveFiltersToLocalStorage();
    this.currentFilter.next(current);
  }
}
